import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Clock,
  Star,
  ChevronDown,
  ArrowLeft,
  Layers,
  Play,
  Loader2,
  AlertCircle,
  Target,
  HelpCircle,
  Video
} from 'lucide-react'
import { contentApi } from '@/api/content'
import { quizScriptApi } from '@/api/quizScript'
import { LessonSidebar } from '@/components/learn/LessonSidebar'
import { CurriculumSidebar } from '@/components/learn/CurriculumSidebar'
import { Menu, X } from 'lucide-react'
import { getCurriculumIdForCourse } from '@/utils/curriculumMapping'

// 학습 시간을 시간 단위로 변환
function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '-'
  if (minutes < 60) return `${minutes}분`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`
}

// LessonItemWithComponents 제거 - 더 이상 필요하지 않을 수 있지만, 일단 유지하고 사용하지 않을 경우 나중에 정리
function LessonItemWithComponents({
  lesson,
  lessonIndex
}: {
  lesson: { id: number; title: string; estimatedDurationMinutes?: number | null; qid?: string | null }
  lessonIndex: number
}) {
  // 퀴즈 개수 조회
  const { data: quizData } = useQuery({
    queryKey: ['lesson-quizzes-count', lesson.id],
    queryFn: async () => {
      try {
        // QuizScript[] 배열 반환
        const scripts = await quizScriptApi.getContentQuizzes?.(lesson.id, undefined, false)
        return scripts?.length || 0
      } catch {
        return 0
      }
    },
    enabled: !!lesson.id && !!quizScriptApi.getContentQuizzes,
    staleTime: 5 * 60 * 1000,
  })

  const quizCount = quizData || 0
  const hasVideo = !!lesson.qid

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0">
      {/* Lesson Number */}
      <div className="w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-600 text-sm font-medium shrink-0">
        {lessonIndex + 1}
      </div>

      {/* Lesson Info */}
      <div className="flex-1 min-w-0">
        <Link
          to={`/learn/lesson/${lesson.id}`}
          className="block"
        >
          <h4 className="text-gray-900 truncate hover:text-primary-600 transition-colors">
            {lesson.title}
          </h4>
        </Link>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDuration(lesson.estimatedDurationMinutes)}
          </span>
          {hasVideo && (
            <Link
              to={`/learn/lesson/${lesson.id}?mode=video`}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <Video className="w-3 h-3" />
              영상
            </Link>
          )}
          {quizCount > 0 && (
            <Link
              to={`/learn/lesson/${lesson.id}?mode=quiz`}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <HelpCircle className="w-3 h-3" />
              문제 {quizCount}개
            </Link>
          )}
        </div>
      </div>

      {/* Play Icon */}
      <Link
        to={`/learn/lesson/${lesson.id}`}
        className="shrink-0"
      >
        <Play className="w-4 h-4 text-primary-600 hover:text-primary-700 transition-colors" />
      </Link>
    </div>
  )
}

export function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set())
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  const {
    data: course,
    isLoading,
    error
  } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => contentApi.getCourseDetail(Number(courseId)),
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000,
  })

  // 모듈 확장/축소 토글
  const toggleModule = (moduleId: number) => {
    setExpandedModules(prev => {
      const next = new Set(prev)
      if (next.has(moduleId)) {
        next.delete(moduleId)
      } else {
        next.add(moduleId)
      }
      return next
    })
  }

  // 모두 확장
  const expandAll = () => {
    if (course) {
      setExpandedModules(new Set(course.modules.map(m => m.id)))
    }
  }

  // 모두 축소
  const collapseAll = () => {
    setExpandedModules(new Set())
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-gray-600">콘텐츠를 불러오는 중...</p>
      </div>
    )
  }

  // 에러 상태
  if (error || !course) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-gray-900 font-medium mb-2">코스를 찾을 수 없습니다</p>
        <Link to="/learn" className="text-primary-600 hover:underline">
          목록으로 돌아가기
        </Link>
      </div>
    )
  }

  const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons.length, 0)
  // Determine Curriculum Context
  const derivedCurriculumId = course?.curriculumId || (courseId ? getCurriculumIdForCourse(Number(courseId)) : null)
  const firstLesson = course.modules[0]?.lessons[0]

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 h-full border-r border-gray-200 bg-white shrink-0">
        {derivedCurriculumId ? (
          <CurriculumSidebar
            curriculumId={derivedCurriculumId}
            activeCourseId={Number(courseId)}
            activeLessonId={-1}
          />
        ) : (
          <LessonSidebar
            courseId={Number(courseId)}
            currentLessonId={-1}
          />
        )}
      </div>

      {/* Mobile Sidebar */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white shadow-xl z-50 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-bold text-lg">목차</span>
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            {derivedCurriculumId ? (
              <CurriculumSidebar
                curriculumId={derivedCurriculumId}
                activeCourseId={Number(courseId)}
                activeLessonId={-1}
                className="border-none w-full"
              />
            ) : (
              <LessonSidebar
                courseId={Number(courseId)}
                currentLessonId={-1}
                className="border-none w-full"
              />
            )}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar relative">
        {/* Mobile Header (Sidebar Toggle) */}
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="font-bold text-gray-900 truncate max-w-[200px]">
              {course.title}
            </h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
          {/* Back Button */}
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>학습 목록으로</span>
          </Link>

          {/* Header */}
          <div className="card p-6 bg-linear-to-r from-green-50 to-emerald-100">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs font-medium rounded-full">
                    코스
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  {course.title}
                </h1>
                <p className="text-gray-700 mb-4">
                  {course.description || '수학의 핵심 개념을 체계적으로 학습합니다.'}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <Layers className="w-4 h-4 text-primary-600" />
                    <span className="text-gray-700">{course.modules.length}개 모듈</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <Play className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700">{totalLessons}개 강의</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-gray-700">{formatDuration(course.estimatedDurationMinutes)}</span>
                  </div>
                  {course.starRating && (
                    <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-gray-700">{course.starRating}점</span>
                    </div>
                  )}
                </div>

                {/* Learning Objectives */}
                {course.learningObjectives && (
                  <div className="mt-4 p-4 bg-white/50 rounded-lg">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-primary-600" />
                      학습 목표
                    </h3>
                    <p className="text-sm text-gray-700">{course.learningObjectives}</p>
                  </div>
                )}
              </div>

              {/* Start Button */}
              {firstLesson && (
                <div className="flex md:flex-col justify-center items-center gap-4">
                  <Link
                    to={`/learn/lesson/${firstLesson.id}`}
                    className="btn-primary text-lg px-8 py-3 flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    학습 시작
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Modules List */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary-600" />
                모듈 목록
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded-sm hover:bg-gray-100"
                >
                  모두 펼치기
                </button>
                <button
                  onClick={collapseAll}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded-sm hover:bg-gray-100"
                >
                  모두 접기
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {course.modules.map((module, moduleIndex) => {
                const isExpanded = expandedModules.has(module.id)

                return (
                  <div key={module.id} className="card overflow-hidden">
                    {/* Module Header */}
                    <button
                      onClick={() => toggleModule(module.id)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                    >
                      {/* Module Number */}
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold shrink-0">
                        {moduleIndex + 1}
                      </div>

                      {/* Module Info */}
                      <div className="flex-1 text-left min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {module.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Play className="w-3.5 h-3.5" />
                            {module.lessons.length}개 강의
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDuration(module.estimatedDurationMinutes)}
                          </span>
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {/* Lessons List */}
                    {isExpanded && module.lessons.length > 0 && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <LessonItemWithComponents
                            key={lesson.id}
                            lesson={lesson}
                            lessonIndex={lessonIndex}
                          />
                        ))}
                      </div>
                    )}

                    {/* Empty Lessons */}
                    {isExpanded && module.lessons.length === 0 && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4 text-center text-sm text-gray-500">
                        등록된 강의가 없습니다
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </section>

          {/* Empty State */}
          {course.modules.length === 0 && (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Layers className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                등록된 모듈이 없습니다
              </h3>
              <p className="text-gray-500">
                콘텐츠가 준비 중입니다. 잠시 후 다시 확인해주세요.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

